import { I18nManager } from "../../services/i18n-manager";
import { ProductService } from "../../services/product-service";
import { DialogManager } from "./dialog-manager";

interface OrderItem {
  product_id: string;
  name: string;
  quantity: number;
  price_sek: number;
  options?: Record<string, any>;
}

interface ShippingAddress {
  line1?: string;
  address?: string;
  city?: string;
  postal_code?: string;
  postal?: string;
  country?: string;
}

interface OrderData {
  id: string;
  customer_email: string;
  customer_name: string;
  items: OrderItem[];
  subtotal_sek?: number;
  shipping_sek?: number;
  total_sek: number;
  shipping_address?: ShippingAddress;
}

export class OrderConfirmationDialog {
  private dialogElement: HTMLElement;
  private i18nManager: I18nManager;
  private dialogManager: DialogManager;
  private productService: ProductService | null = null;

  // DOM elements
  private titleElement: HTMLElement | null = null;
  private thankYouElement: HTMLElement | null = null;
  private greetingElement: HTMLElement | null = null;
  private receivedElement: HTMLElement | null = null;
  private orderNumberElement: HTMLElement | null = null;
  private orderNumberLabelElement: HTMLElement | null = null;
  private customerNameElement: HTMLElement | null = null;
  private customerEmailElement: HTMLElement | null = null;
  private itemsListElement: HTMLElement | null = null;
  private itemsHeadingElement: HTMLElement | null = null;
  private totalAmountElement: HTMLElement | null = null;
  private totalLabelElement: HTMLElement | null = null;
  private subtotalAmountElement: HTMLElement | null = null;
  private subtotalLabelElement: HTMLElement | null = null;
  private shippingCostElement: HTMLElement | null = null;
  private shippingCostLabelElement: HTMLElement | null = null;
  private shippingAddressLabelElement: HTMLElement | null = null;
  private addressElement: HTMLElement | null = null;
  private addressLabelElement: HTMLElement | null = null;
  private cityElement: HTMLElement | null = null;
  private cityLabelElement: HTMLElement | null = null;
  private postalElement: HTMLElement | null = null;
  private postalLabelElement: HTMLElement | null = null;
  private countryElement: HTMLElement | null = null;
  private countryLabelElement: HTMLElement | null = null;
  private shippingNoteElement: HTMLElement | null = null;
  private questionsNoteElement: HTMLElement | null = null;
  private continueButton: HTMLButtonElement | null = null;

  constructor(
    i18nManager: I18nManager,
    dialogManager: DialogManager,
    productService?: ProductService
  ) {
    this.dialogElement = document.getElementById(
      "order-confirmation-dialog"
    ) as HTMLElement;
    this.i18nManager = i18nManager;
    this.dialogManager = dialogManager;
    this.productService = productService || null;

    if (!this.dialogElement) {
      console.error("Order confirmation dialog element not found!");
      return;
    }

    // Ensure dialog starts hidden
    this.dialogElement.setAttribute("aria-hidden", "true");

    this.initializeElements();
    this.setupEventListeners();
  }

  init(): void {
    // Dialog is ready to use
    // Initialization happens in constructor
  }

  private initializeElements(): void {
    this.titleElement = this.dialogElement.querySelector(
      "#order-confirmation-title"
    );
    this.thankYouElement = this.dialogElement.querySelector("#order-thank-you");
    this.greetingElement = this.dialogElement.querySelector("#order-greeting");
    this.receivedElement = this.dialogElement.querySelector("#order-received");
    this.orderNumberElement = this.dialogElement.querySelector(
      "#order-confirmation-number"
    );
    this.orderNumberLabelElement = this.dialogElement.querySelector(
      "#order-number-label"
    );
    this.customerNameElement = this.dialogElement.querySelector(
      "#order-customer-name"
    );
    this.customerEmailElement = this.dialogElement.querySelector(
      "#order-customer-email"
    );
    this.shippingAddressLabelElement = this.dialogElement.querySelector(
      "#order-shipping-address-label"
    );
    this.addressElement = this.dialogElement.querySelector("#order-address");
    this.addressLabelElement = this.dialogElement.querySelector(
      "#order-address-label"
    );
    this.cityElement = this.dialogElement.querySelector("#order-city");
    this.cityLabelElement =
      this.dialogElement.querySelector("#order-city-label");
    this.postalElement = this.dialogElement.querySelector("#order-postal");
    this.postalLabelElement = this.dialogElement.querySelector(
      "#order-postal-label"
    );
    this.countryElement = this.dialogElement.querySelector("#order-country");
    this.countryLabelElement = this.dialogElement.querySelector(
      "#order-country-label"
    );
    this.itemsListElement =
      this.dialogElement.querySelector("#order-items-list");
    this.itemsHeadingElement = this.dialogElement.querySelector(
      "#order-items-heading"
    );
    this.totalAmountElement = this.dialogElement.querySelector(
      "#order-total-amount"
    );
    this.totalLabelElement =
      this.dialogElement.querySelector("#order-total-label");
    this.subtotalAmountElement = this.dialogElement.querySelector(
      "#order-subtotal-amount"
    );
    this.subtotalLabelElement = this.dialogElement.querySelector(
      "#order-subtotal-label"
    );
    this.shippingCostElement = this.dialogElement.querySelector(
      "#order-shipping-cost-amount"
    );
    this.shippingCostLabelElement = this.dialogElement.querySelector(
      "#order-shipping-cost-label"
    );
    this.shippingNoteElement = this.dialogElement.querySelector(
      "#order-shipping-note"
    );
    this.questionsNoteElement = this.dialogElement.querySelector(
      "#order-questions-note"
    );
    this.continueButton = this.dialogElement.querySelector(
      "#order-confirmation-continue-btn"
    ) as HTMLButtonElement;
  }

  private setupEventListeners(): void {
    // Dialog open event
    this.dialogElement.addEventListener("dialog:open", (event: any) => {
      const customEvent = event as CustomEvent;
      let orderData = customEvent.detail as OrderData;

      // Fallback: also check data attribute if detail is not available
      if (!orderData) {
        const dataAttr = this.dialogElement.getAttribute("data-dialog-data");
        if (dataAttr) {
          try {
            orderData = JSON.parse(dataAttr) as OrderData;
          } catch (err) {
            console.error("Failed to parse dialog data:", err);
          }
        }
      }

      // Update translations first (without customer-specific data)
      this.updateTranslations();
      // Then render order with customer-specific data
      if (orderData) {
        this.renderOrder(orderData);
      } else {
        console.error("Order confirmation dialog opened without order data");
      }
    });

    // Dialog close event
    this.dialogElement.addEventListener("dialog:close", () => {
      // Clear order data when closing
      this.clearOrder();
    });

    // Continue shopping button
    this.continueButton?.addEventListener("click", () => {
      this.dialogManager.closeDialog();
    });

    // Language changes
    this.i18nManager.onLanguageChange(() => this.updateTranslations());
  }

  private renderOrder(orderData: OrderData): void {
    // Update order number - use short format (first 8 characters)
    if (this.orderNumberElement) {
      this.orderNumberElement.textContent = orderData.id
        ? orderData.id.substring(0, 8).toUpperCase()
        : "Unknown";
    }

    // Update customer info
    if (this.customerNameElement) {
      this.customerNameElement.textContent = orderData.customer_name || "-";
    }
    if (this.customerEmailElement) {
      this.customerEmailElement.textContent = orderData.customer_email || "-";
    }

    // Update shipping address
    if (orderData.shipping_address) {
      const address = orderData.shipping_address;
      if (this.addressElement) {
        this.addressElement.textContent =
          address.line1 || address.address || "-";
      }
      if (this.cityElement) {
        this.cityElement.textContent = address.city || "-";
      }
      if (this.postalElement) {
        this.postalElement.textContent =
          address.postal_code || address.postal || "-";
      }
      if (this.countryElement) {
        this.countryElement.textContent = address.country || "-";
      }
    } else {
      // No shipping address provided
      if (this.addressElement) this.addressElement.textContent = "-";
      if (this.cityElement) this.cityElement.textContent = "-";
      if (this.postalElement) this.postalElement.textContent = "-";
      if (this.countryElement) this.countryElement.textContent = "-";
    }

    // Update greeting with customer name
    if (this.greetingElement) {
      const greeting = this.i18nManager.t("order_confirmation.greeting");
      const customerName = orderData.customer_name || "Customer";
      this.greetingElement.textContent = greeting.replace(
        "{name}",
        customerName
      );
    }

    // Render order items
    this.renderOrderItems(orderData.items);

    // Calculate subtotal if not provided
    const subtotal =
      orderData.subtotal_sek ||
      orderData.items.reduce(
        (sum, item) => sum + item.price_sek * item.quantity,
        0
      );
    const shipping = orderData.shipping_sek || (subtotal > 0 ? 49 : 0);

    // Update subtotal
    if (this.subtotalAmountElement) {
      this.subtotalAmountElement.textContent =
        this.i18nManager.formatCurrency(subtotal);
    }

    // Update shipping cost
    if (this.shippingCostElement) {
      this.shippingCostElement.textContent =
        this.i18nManager.formatCurrency(shipping);
    }

    // Update total
    if (this.totalAmountElement) {
      const totalText = this.i18nManager.t("order_confirmation.total");
      const formattedAmount = this.i18nManager.formatCurrency(
        orderData.total_sek
      );
      // Remove "SEK" from formatted amount if it's already included, or format properly
      this.totalAmountElement.textContent = totalText.replace(
        "{amount}",
        formattedAmount.replace(" SEK", "")
      );
    }
  }

  private renderOrderItems(items: OrderItem[]): void {
    if (!this.itemsListElement) return;

    this.itemsListElement.innerHTML = items
      .map((item) => this.renderOrderItem(item))
      .join("");
  }

  private renderOrderItem(item: OrderItem): string {
    // Get localized product name if productService is available
    let productName = item.name;
    if (this.productService) {
      const product = this.productService
        .getProducts()
        .find((p) => p.id === item.product_id);
      if (product) {
        productName = this.productService.getLocalizedName(product);
      }
    }

    // Render options
    const optionsHtml = this.renderItemOptions(
      item.options || {},
      item.product_id
    );

    const itemTotal = item.price_sek * item.quantity;
    const quantityLabel =
      this.i18nManager.t("order_confirmation.quantity") || "Quantity";
    const priceLabel =
      this.i18nManager.t("order_confirmation.price") || "Price";

    return `
      <div class="order-item">
        <div class="order-item-details">
          <div class="order-item-name">${productName}</div>
          ${
            optionsHtml
              ? `<div class="order-item-options">${optionsHtml}</div>`
              : ""
          }
        </div>
        <div class="order-item-quantity">
          <span class="order-item-label">${quantityLabel}:</span>
          <span class="order-item-value">${item.quantity}</span>
        </div>
        <div class="order-item-price">
          <span class="order-item-label">${priceLabel}:</span>
          <span class="order-item-value">${this.i18nManager.formatCurrency(
            item.price_sek
          )}</span>
        </div>
        <div class="order-item-total">
          <span class="order-item-label">Total:</span>
          <span class="order-item-value">${this.i18nManager.formatCurrency(
            itemTotal
          )}</span>
        </div>
      </div>
    `;
  }

  private renderItemOptions(
    options: Record<string, any>,
    productId?: string
  ): string {
    if (!options || Object.keys(options).length === 0) {
      return "";
    }

    // Try to get localized option names and values if productService is available
    if (this.productService && productId) {
      const product = this.productService
        .getProducts()
        .find((p) => p.id === productId);
      if (product && product.options) {
        return Object.entries(options)
          .map(([key, value]) => {
            const optionAttribute = product.options[key];
            if (optionAttribute) {
              const optionName =
                this.productService!.getLocalizedOptionName(optionAttribute);
              const optionValueLabel =
                this.productService!.getLocalizedOptionValueByValue(
                  optionAttribute,
                  value as string
                );
              return `<div class="order-item-option"><strong>${optionName}:</strong> ${optionValueLabel}</div>`;
            }
            // Fallback to old behavior if option not found
            return `<div class="order-item-option"><strong>${key}:</strong> ${value}</div>`;
          })
          .join("");
      }
    }

    // Fallback to old behavior if productService not available or product not found
    return Object.entries(options)
      .map(
        ([key, value]) =>
          `<div class="order-item-option"><strong>${key}:</strong> ${value}</div>`
      )
      .join("");
  }

  private clearOrder(): void {
    if (this.orderNumberElement) {
      this.orderNumberElement.textContent = "-";
    }
    if (this.customerNameElement) {
      this.customerNameElement.textContent = "-";
    }
    if (this.customerEmailElement) {
      this.customerEmailElement.textContent = "-";
    }
    if (this.addressElement) {
      this.addressElement.textContent = "-";
    }
    if (this.cityElement) {
      this.cityElement.textContent = "-";
    }
    if (this.postalElement) {
      this.postalElement.textContent = "-";
    }
    if (this.countryElement) {
      this.countryElement.textContent = "-";
    }
    if (this.itemsListElement) {
      this.itemsListElement.innerHTML = "";
    }
    if (this.subtotalAmountElement) {
      this.subtotalAmountElement.textContent = "-";
    }
    if (this.shippingCostElement) {
      this.shippingCostElement.textContent = "-";
    }
    if (this.totalAmountElement) {
      this.totalAmountElement.textContent = "-";
    }
    if (this.greetingElement) {
      this.greetingElement.textContent = "";
    }
  }

  private updateTranslations(): void {
    // Update title
    if (this.titleElement) {
      this.titleElement.textContent =
        this.i18nManager.t("order_confirmation.title") || "Order Confirmation";
    }

    // Update thank you message
    if (this.thankYouElement) {
      this.thankYouElement.textContent =
        this.i18nManager.t("order_confirmation.thank_you") ||
        "Thank you for your order!";
    }

    // Update received message
    if (this.receivedElement) {
      this.receivedElement.textContent =
        this.i18nManager.t("order_confirmation.received") ||
        "Your order has been received and is being processed. Here are the details:";
    }

    // Update order number label
    if (this.orderNumberLabelElement) {
      this.orderNumberLabelElement.textContent =
        this.i18nManager.t("order_confirmation.order_number") || "Order #";
    }

    // Update items heading
    if (this.itemsHeadingElement) {
      this.itemsHeadingElement.textContent =
        this.i18nManager.t("order_confirmation.items") || "Items";
    }

    // Hide shipping note and questions note
    if (this.shippingNoteElement) {
      this.shippingNoteElement.style.display = "none";
    }

    if (this.questionsNoteElement) {
      this.questionsNoteElement.style.display = "none";
    }

    // Update subtotal and shipping labels
    if (this.subtotalLabelElement) {
      this.subtotalLabelElement.textContent =
        this.i18nManager.t("order_confirmation.subtotal") || "Subtotal:";
    }
    if (this.shippingCostLabelElement) {
      this.shippingCostLabelElement.textContent =
        this.i18nManager.t("order_confirmation.shipping") || "Shipping:";
    }

    // Update shipping address labels
    if (this.shippingAddressLabelElement) {
      this.shippingAddressLabelElement.textContent =
        this.i18nManager.t("order_confirmation.shipping_address") ||
        "Shipping Address";
    }
    if (this.addressLabelElement) {
      this.addressLabelElement.textContent =
        this.i18nManager.t("order_confirmation.address") || "Address:";
    }
    if (this.cityLabelElement) {
      this.cityLabelElement.textContent =
        this.i18nManager.t("order_confirmation.city") || "City:";
    }
    if (this.postalLabelElement) {
      this.postalLabelElement.textContent =
        this.i18nManager.t("order_confirmation.postal_code") || "Postal Code:";
    }
    if (this.countryLabelElement) {
      this.countryLabelElement.textContent =
        this.i18nManager.t("order_confirmation.country") || "Country:";
    }

    // Update continue button
    if (this.continueButton) {
      this.continueButton.textContent =
        this.i18nManager.t("order_confirmation.continue_shopping") ||
        "Continue Shopping";
    }
  }
}
